/**
 * Communications hub hooks (F50) — the citizen inbox and the company's
 * per-talent Communications tab.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import type {
  InboxItem,
  InboxItemState,
  InboxPage,
  InboxUnreadCount,
  InboxView,
  TalentCommunicationsPage,
} from '@tonnie/types';

import { TonnieApiError } from '../client/base';
import { useApiClient } from '../providers/SdkProvider';
import { CommunicationsService } from '../services/communications.service';
import { communicationsKeys } from './communications-keys';
import { notificationKeys } from './useNotifications';

/**
 * Everything that shows a message's read state: the inbox lists and the one
 * unread number (bell, page) — and the notification cards, which hold
 * an in-app message's state. Refreshed together, so no two surfaces disagree.
 */
function refreshMessageState(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: communicationsKeys.inbox });
  void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
}

export { communicationsKeys };

/** The caller's inbox, a page at a time, newest first. */
export function useInbox(view: InboxView) {
  const client = useApiClient();
  const service = new CommunicationsService(client);
  return useInfiniteQuery({
    queryKey: communicationsKeys.inboxList(view),
    queryFn: ({ pageParam }) => service.getInbox({ view, cursor: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (last: InboxPage) => last.nextCursor ?? undefined,
  });
}

/**
 * One message, for the reader — by id, so it stays open whatever happens to
 * the list it was opened from (reading it moves it out of Unread; archiving,
 * out of Inbox). Opened from a list, it shows the row the list already holds
 * at once, and the server's copy replaces it when it lands. A message that is
 * not the person's (or no longer exists) is a 404, never retried.
 */
export function useInboxItem(id: string | null) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const service = new CommunicationsService(client);
  return useQuery<InboxItem>({
    queryKey: communicationsKeys.inboxItem(id ?? ''),
    queryFn: () => service.getInboxItem(id as string),
    enabled: id !== null,
    placeholderData: () => (id ? cachedListRow(queryClient, id) : undefined),
    retry: (count, error) => !(error instanceof TonnieApiError && error.isNotFound) && count < 2,
  });
}

/** The row for `id` in any cached inbox list, if one holds it. */
function cachedListRow(queryClient: QueryClient, id: string): InboxItem | undefined {
  for (const [, data] of queryClient.getQueriesData<InfiniteData<InboxPage>>(inboxLists)) {
    for (const page of data?.pages ?? []) {
      const found = page.items.find((item) => item.id === id);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * The person's ONE unread number — every unread message, in-app and email,
 * from every tenant they are related to. The bell and the inbox page (web and
 * mobile) all read this one query, so they change together. Polled like the bell
 * always was; paused while the request errors.
 */
export function useInboxUnreadCount() {
  const client = useApiClient();
  const service = new CommunicationsService(client);
  return useQuery<number>({
    queryKey: communicationsKeys.unreadCount(),
    queryFn: async () => (await service.getInboxUnreadCount()).unreadCount,
    refetchInterval: (query) => (query.state.status === 'error' ? false : 60_000),
  });
}

/**
 * Mark every unread message read. The count drops to 0 at once (rolled back if
 * the request fails), then every message surface is refreshed from the server.
 */
export function useMarkAllInboxRead() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const service = new CommunicationsService(client);
  return useMutation<InboxUnreadCount, Error, void, { previous: number | undefined }>({
    mutationFn: () => service.markAllInboxRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: communicationsKeys.unreadCount() });
      const previous = queryClient.getQueryData<number>(communicationsKeys.unreadCount());
      queryClient.setQueryData<number>(communicationsKeys.unreadCount(), 0);
      return { previous };
    },
    onError: (_error, _vars, context) => {
      queryClient.setQueryData(communicationsKeys.unreadCount(), context?.previous);
    },
    onSettled: () => refreshMessageState(queryClient),
  });
}

export type InboxAction = 'read' | 'unread' | 'archive' | 'restore';

/** Every cached inbox list (each view), for a write that touches them all. */
const inboxLists = { queryKey: [...communicationsKeys.inbox, 'list'] } as const;

interface ReadSnapshot {
  lists: Array<[QueryKey, InfiniteData<InboxPage> | undefined]>;
  item: InboxItem | undefined;
  unreadCount: number | undefined;
}

/**
 * Read, mark unread, archive or restore one message. An in-app card's state
 * lives on the notification itself, so the bell's queries are refreshed with
 * the inbox's.
 *
 * Reading and marking unread are optimistic: the row flips in EVERY cached
 * view and in the open reader, and the one unread number moves, before the
 * server answers — expanding a card in the bell (F49) or opening a message
 * must not wait on a round trip. Each is rolled back to its own snapshot if
 * the request fails. Archive and restore wait for the server; its answer is
 * written into the open reader at once, then every surface is refreshed.
 */
export function useInboxAction() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const service = new CommunicationsService(client);
  return useMutation<
    InboxItemState,
    Error,
    { id: string; action: InboxAction },
    ReadSnapshot | undefined
  >({
    mutationFn: ({ id, action }) => {
      switch (action) {
        case 'read':
          return service.markInboxItemRead(id);
        case 'unread':
          return service.markInboxItemUnread(id);
        case 'archive':
          return service.archiveInboxItem(id);
        case 'restore':
          return service.restoreInboxItem(id);
        default: {
          const unreachable: never = action;
          throw new Error(`unknown inbox action ${String(unreachable)}`);
        }
      }
    },
    onMutate: async ({ id, action }) => {
      if (action !== 'read' && action !== 'unread') return undefined;
      await queryClient.cancelQueries({ queryKey: communicationsKeys.inbox });
      const itemKey = communicationsKeys.inboxItem(id);
      const snapshot: ReadSnapshot = {
        lists: queryClient.getQueriesData<InfiniteData<InboxPage>>(inboxLists),
        item: queryClient.getQueryData<InboxItem>(itemKey),
        unreadCount: queryClient.getQueryData<number>(communicationsKeys.unreadCount()),
      };
      const readAt = action === 'read' ? new Date().toISOString() : null;
      // Only a row whose state actually changes moves the number, and only
      // once; an archived message is in no unread number at all.
      const flips = (item: InboxItem) => (action === 'read') === (item.readAt === null);
      let counted: boolean | null = null;
      const flip = (item: InboxItem): InboxItem => {
        counted ??= !item.archived;
        return { ...item, readAt };
      };
      queryClient.setQueriesData<InfiniteData<InboxPage>>(inboxLists, (data) => {
        if (!data) return data;
        let touched = false;
        const pages = data.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => {
            if (item.id !== id || !flips(item)) return item;
            touched = true;
            return flip(item);
          }),
        }));
        return touched ? { ...data, pages } : data;
      });
      if (snapshot.item && flips(snapshot.item)) {
        queryClient.setQueryData<InboxItem>(itemKey, flip(snapshot.item));
      }
      if (counted && snapshot.unreadCount !== undefined) {
        queryClient.setQueryData<number>(
          communicationsKeys.unreadCount(),
          Math.max(snapshot.unreadCount + (action === 'read' ? -1 : 1), 0)
        );
      }
      return snapshot;
    },
    onSuccess: (state) => {
      queryClient.setQueryData<InboxItem>(communicationsKeys.inboxItem(state.id), (item) =>
        item ? { ...item, readAt: state.readAt, archived: state.archived } : item
      );
    },
    onError: (_error, { id }, snapshot) => {
      if (!snapshot) return;
      for (const [key, data] of snapshot.lists) queryClient.setQueryData(key, data);
      queryClient.setQueryData(communicationsKeys.inboxItem(id), snapshot.item);
      queryClient.setQueryData(communicationsKeys.unreadCount(), snapshot.unreadCount);
    },
    onSettled: () => refreshMessageState(queryClient),
  });
}

/** What the company sent one talent, a page at a time. */
export function useTalentCommunications(talentId: string) {
  const client = useApiClient();
  const service = new CommunicationsService(client);
  return useInfiniteQuery({
    queryKey: communicationsKeys.talent(talentId),
    queryFn: ({ pageParam }) =>
      service.getTalentCommunications(talentId, { cursor: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (last: TalentCommunicationsPage) => last.nextCursor ?? undefined,
    enabled: !!talentId,
  });
}
